import { useControllableState } from "@chakra-ui/react"
import Button from "@pathwright/ui/src/components/button/Button"
import Dropdown from "@pathwright/ui/src/components/dropdown"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ui/src/components/tooltip/Tooltip"
import styled from "styled-components"
import { ResourceType } from "../../api/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { getResourceTypeLabel } from "../../resource/utils"

export function ResourceTypeTooltip({
  resourceType
}: {
  resourceType: ResourceType
}) {
  const { t } = useTranslate()

  const courseTooltip = (
    <Tooltip
      title={
        <>
          A Path is a series of steps for completing any kind of learning
          experience: a class, online course, event, skill-based outcome, and
          more.
          <br />
          <br />
          <Button
            styleType="text"
            href="http://www.pathwright.com/"
            target="_blank"
            inverted
          >
            {t("Learn more about kinds of Paths you can make.")}{" "}
            <Pathicon icon="external-link" />
          </Button>
        </>
      }
    >
      <Pathicon icon="info-circle" />
    </Tooltip>
  )

  const collectionTooltip = (
    <Tooltip
      title={
        <>
          A Collection is a "path of paths" through multiple courses.
          Collections are perfect for longer programs or programs where learners
          can choose the order they want to learn in.
          <br />
          <br />
          <Button
            styleType="text"
            href="https://help.pathwright.com/en/articles/2312339-collections-chart-progress-through-an-entire-program"
            target="_blank"
            inverted
          >
            {t("Learn more")} <Pathicon icon="external-link" />
          </Button>
        </>
      }
    >
      <Pathicon icon="info-circle" />
    </Tooltip>
  )

  const tooltip = {
    [ResourceType.Course]: courseTooltip,
    [ResourceType.Collection]: collectionTooltip,
    [ResourceType.Lesson]: null
  }[resourceType]

  return tooltip
}

const ResourceTypeSwitcher = ({
  resourceType: resourceTypeProp,
  onSwitchResourceType,
  prompt: promptProp
}: {
  resourceType?: ResourceType | null
  onSwitchResourceType: (resourceType: ResourceType) => void
  prompt?: string
}) => {
  const { t, tc } = useTranslate()
  const { school } = usePathwrightContext()

  // enforce platform version restriction, only allowing course resource type for versions less than 2.6
  const value =
    ((school.platform_version || 0) < 2.6
      ? ResourceType.Course
      : resourceTypeProp) || ResourceType.Course

  const [resourceType, setResourceType] = useControllableState({
    value: resourceTypeProp ? value : undefined,
    defaultValue: resourceTypeProp ? undefined : value,
    onChange: onSwitchResourceType
  })

  const prompt = promptProp || t("Create a")
  const className = "ResourceTypeSwitcher"

  return (
    <Container className={className}>
      {prompt}
      <Dropdown.Trigger
        alignment="left"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
          e.stopPropagation()
        }
        renderDropdown={() => (
          <Dropdown.Menu style={{ minWidth: "200px" }}>
            <Dropdown.Item
              label={getResourceTypeLabel(ResourceType.Course, tc)}
              onClick={() => setResourceType(ResourceType.Course)}
              meta={t(
                "A series of learning steps grouped in one or more lessons  (a single course, class, training, event, etc.)."
              )}
              closeOnClick
            />
            <Dropdown.Item
              label={getResourceTypeLabel(ResourceType.Collection, tc)}
              onClick={() => setResourceType(ResourceType.Collection)}
              meta={t(
                "A series of Paths that make up a larger program, conference, degree, etc."
              )}
              closeOnClick
            />
          </Dropdown.Menu>
        )}
      >
        <Button styleType="secondary">
          {getResourceTypeLabel(resourceType, tc)}
          <Pathicon icon="chevron-down" />
        </Button>
      </Dropdown.Trigger>
      <ResourceTypeTooltip resourceType={resourceType} />
    </Container>
  )
}

ResourceTypeSwitcher.displayName = "ResourceTypeSwitcher"

const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;

  > .Pathicon:first-child,
  > .Button .Button__content-wrapper .Pathicon:first-child {
    margin-right: 0.4em;
  }
  > .Dropdown__Trigger > .Button {
    margin-left: 0.4em;
    margin-right: 0.4em;
    // min-width: 200px;
  }

  .Tooltip__trigger {
    display: inline-flex !important;
    padding-top: 6px;
    padding-bottom: 6px;
  }
`

export default ResourceTypeSwitcher
